/**
 * Whether the "Sources" picker should be shown at all (ACOP-142).
 *
 * Deliberately free of React so the rule can be unit-tested directly — the same reasoning as
 * utils/nav.ts and utils/sourceLabels.ts.
 *
 * An end-user's capabilities list is the knowledge base and nothing else, so there is nothing to
 * choose between and a picker would be an empty control (or, worse, one that hints other sources
 * exist). Employees still get every source and keep the picker.
 *
 * This is presentation only. It never decides what a user may search: AVS OS derives that from the
 * caller's roles, and copilot-api refuses to forward a non-employee's own source selection.
 *
 * Counts the AVAILABLE sources, because an unavailable one ("Coming soon") cannot be picked either.
 */
export function showSourcePicker(capabilities: unknown): boolean {
  if (!Array.isArray(capabilities)) return false;
  return capabilities.filter((c) => c && c.available).length > 1;
}

/**
 * Whether a source is one of the Beta Preview EXTRAS (ACOP-140).
 *
 * The BASE is the public git knowledge base — every `kb_*` source EXCEPT the internal one — and it is
 * exactly what a customer gets. Everything else is an extra an employee can switch on with Beta
 * Preview: the internal KB (`kb_internal_knowledge`, employee-only, never for customers), product
 * knowledge, support cases, change requests and feature requests. The RAG cannot yet combine those
 * reliably, so they stay eliminated unless the toggle is on (David, 2026-10-13).
 *
 * Presentation only — AVS OS decides what a user may search from the employee role (ACOP-142 /
 * OS-188). Matches on `key`, which is the contract the picker sends back as `sourceTypes`. A future
 * INTERNAL KB area has to be added here until AVS OS flags each capability's audience.
 */
const INTERNAL_KB_KEYS = new Set(["kb_internal_knowledge"]);

/** A capability as AVS OS returns it: only `key` and `available` are read here, the rest passes through. */
export type SourceCapability = { key?: string; available?: unknown; [field: string]: unknown };

export function isBetaSource(capability: unknown): boolean {
  const raw = capability ? (capability as SourceCapability).key : undefined;
  const key = typeof raw === "string" ? raw : "";
  if (!key.startsWith("kb_")) return true;
  return INTERNAL_KB_KEYS.has(key);
}

/**
 * The sources to offer and search: the base always, plus the extras only when Beta Preview is on.
 * A non-employee never has extras in the list (AVS OS withholds them), so for them this is the list.
 */
export function visibleSources(capabilities: unknown, betaOn: boolean): SourceCapability[] {
  if (!Array.isArray(capabilities)) return [];
  return betaOn ? capabilities : capabilities.filter((c) => !isBetaSource(c));
}

/**
 * The `sourceTypes` a chat request carries (ACOP-140, AC-6).
 *
 * `undefined` means "no filter" and the server then searches everything the caller may see — for an
 * employee that includes the Beta Preview extras. So with the toggle OFF the request must name the
 * base keys explicitly whenever extras exist, even when the selection is empty (every picker box
 * unticked) or still holds a key the toggle has just hidden. Toggle ON, and every non-employee list
 * (no extras), behave exactly as before: a strict non-empty subset is sent, anything else is undefined.
 */
export function requestSourceTypes(
  capabilities: unknown,
  selected: string[],
  betaOn: boolean
): string[] | undefined {
  const all = Array.isArray(capabilities) ? capabilities : [];
  let chosen = selected;
  if (!betaOn) {
    const visible = visibleSources(all, false);
    const visibleKeys = new Set(visible.map((c) => c.key));
    chosen = selected.filter((k) => visibleKeys.has(k));
    if (chosen.length === 0) chosen = visible.filter((c) => c.available).map((c) => c.key);
  }
  return chosen.length > 0 && chosen.length < all.length ? chosen : undefined;
}

/**
 * Whether a send must WAIT because the source list has not arrived yet (ACOP-140).
 *
 * With Beta Preview off, an employee's request is narrowed by naming the base keys, and that needs
 * the capability list. Until it loads there is nothing to name, so `sourceTypes` would go out
 * undefined — which AVS OS reads as "every source I may use", i.e. all the extras for an employee.
 * So that one case holds the send. A non-employee never needs it (AVS OS returns them the KB only,
 * list or no list), and with the toggle ON "all sources" is exactly what was asked for.
 *
 * If the list never loads, an employee can still flip Beta Preview on, or reload; the alternative is
 * quietly searching sources the toggle says are off.
 */
export function sourcesStillLoading(capabilities: unknown, isEmployee: boolean, betaOn: boolean): boolean {
  if (!isEmployee || betaOn) return false;
  return !Array.isArray(capabilities) || capabilities.length === 0;
}
